/**
 * ChatBody — content+theme+behavior+code для chat-виджета.
 * Контент: title, greeting, placeholder, offline_message, аватар оператора,
 * звук, ПРИВЯЗКА БОТ-СЦЕНАРИЯ (восстановлено — было потеряно в прошлой версии).
 */
import { defaultTemplateMeta } from '@inbx/shared/schemas';
import { type ReactNode } from 'react';

import { useBotScenarios } from '../api/queries';

import { type WidgetBodyContract, type WidgetDraft } from './editor-shell';
import { AssetUpload, BehaviorTab, CodeTab, IdentitySecretSection, ThemeTab } from './shared-tabs';

interface ChatContent {
  title?: string;
  greeting?: string;
  placeholder?: string;
  offline_message?: string;
  avatar_url?: string;
  show_agent_avatar?: boolean;
  enable_sound?: boolean;
}

export function makeChatBody(args: {
  widgetId?: string;
  widgetPublicKey: string;
  workspaceId: string;
  workspaceSlug?: string;
}): WidgetBodyContract {
  return {
    getTabs() {
      return [
        { key: 'content', label: 'Содержимое' },
        { key: 'theme', label: 'Тема' },
        { key: 'behavior', label: 'Поведение' },
        { key: 'code', label: 'Код' },
      ];
    },
    defaultDraft() {
      const t = defaultTemplateMeta('chat');
      return {
        themeConfig: { template_id: t.id },
        contentConfig: { ...(t.defaultContent as WidgetDraft['contentConfig']) },
        behaviorConfig: { ...(t.defaultBehavior ?? {}) } as WidgetDraft['behaviorConfig'],
        mediaConfig: {},
      };
    },
    validate(draft) {
      const errs: string[] = [];
      const c = (draft.contentConfig ?? {}) as ChatContent;
      if (!c.title?.trim()) errs.push('Заголовок чата обязателен');
      return errs;
    },
    renderTab(activeKey, draft, onChange): ReactNode {
      if (activeKey === 'content')
        return (
          <ChatContentTab draft={draft} onChange={onChange} workspaceSlug={args.workspaceSlug} />
        );
      if (activeKey === 'theme') return <ThemeTab draft={draft} onChange={onChange} />;
      if (activeKey === 'behavior') return <BehaviorTab draft={draft} onChange={onChange} />;
      if (activeKey === 'code')
        return (
          <>
            <CodeTab widgetPublicKey={args.widgetPublicKey} workspaceId={args.workspaceId} />
            {args.widgetId && (
              <IdentitySecretSection widgetId={args.widgetId} workspaceSlug={args.workspaceSlug} />
            )}
          </>
        );
      return null;
    },
  };
}

function ChatContentTab({
  draft,
  onChange,
  workspaceSlug,
}: {
  draft: WidgetDraft;
  onChange: (patch: Partial<WidgetDraft>) => void;
  workspaceSlug?: string;
}): JSX.Element {
  const c = (draft.contentConfig ?? {}) as ChatContent;
  const behavior = (draft.behaviorConfig ?? {}) as { scenario_id?: string | null };
  const scenarios = useBotScenarios(workspaceSlug);
  const set = (patch: Partial<ChatContent>): void => {
    onChange({ contentConfig: { ...draft.contentConfig, ...patch } });
  };
  const setScenario = (id: string): void => {
    onChange({ behaviorConfig: { ...draft.behaviorConfig, scenario_id: id || null } });
  };

  return (
    <div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Тексты</div>
        <div className="inbx-editor-row">
          <div>
            <label className="inbx-editor-label">Заголовок окна</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.title ?? ''}
              onChange={(e) => set({ title: e.target.value })}
            />
          </div>
          <div>
            <label className="inbx-editor-label">Аватар по умолчанию</label>
            <AssetUpload
              accept="image/*"
              value={c.avatar_url}
              onChange={(url) => set({ avatar_url: url || undefined })}
            />
            <div className="inbx-editor-help">
              Используется, если у оператора/бота нет своего аватара (приоритет — у оператора).
            </div>
          </div>
        </div>
        <div>
          <label className="inbx-editor-label">Приветственное сообщение</label>
          <textarea
            className="inbx-editor-textarea"
            rows={2}
            value={c.greeting ?? ''}
            onChange={(e) => set({ greeting: e.target.value })}
          />
          <div className="inbx-editor-help">
            Появится в начале диалога. Пусто — приветствия не будет.
          </div>
        </div>
        <div>
          <label className="inbx-editor-label">Подсказка в поле ввода</label>
          <input
            type="text"
            className="inbx-editor-input"
            value={c.placeholder ?? ''}
            onChange={(e) => set({ placeholder: e.target.value })}
          />
        </div>
        <div>
          <label className="inbx-editor-label">Сообщение когда операторы офлайн</label>
          <textarea
            className="inbx-editor-textarea"
            rows={2}
            value={c.offline_message ?? ''}
            onChange={(e) => set({ offline_message: e.target.value })}
          />
        </div>
      </div>

      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Бот-сценарий</div>
        <div className="inbx-editor-help" style={{ marginBottom: 8 }}>
          Сценарий запускается при открытии чата (например, до ответа оператора).
        </div>
        <select
          className="inbx-editor-select"
          value={behavior.scenario_id ?? ''}
          onChange={(e) => setScenario(e.target.value)}
        >
          <option value="">Без сценария</option>
          {(scenarios.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="inbx-editor-section">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={c.show_agent_avatar ?? false}
            onChange={(e) => set({ show_agent_avatar: e.target.checked })}
          />
          Показывать аватар оператора
        </label>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}
        >
          <input
            type="checkbox"
            checked={c.enable_sound ?? true}
            onChange={(e) => set({ enable_sound: e.target.checked })}
          />
          Звук уведомления о новом сообщении
        </label>
      </div>
    </div>
  );
}
